import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '@app/environments/environment';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { map, switchMap, tap } from 'rxjs/operators';
import { TrezorRegistrationDto } from '@app/generated/models/trezor-registration-dto';
import { pickBy } from 'ramda';
import { CSRF_HEADER_NAME } from '@app/shared/const/csrf.const';
import { GuiParams } from '@app/shared/store/gui-params/gui-params-facade.service';
import { SuccessLoginDto } from '@app/generated/models/success-login-dto';
import { UserLoginDto } from '@app/authentication/shared/api/login-data.api';
import { isPlatformBrowser } from '@angular/common';

type UtmParams = Record<string, string | null>;

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  protected readonly loginEndpoint: string = `${environment.apiRoot}/frontend/user/session`;
  protected readonly trezorLoginEndpoint: string = `${environment.apiRoot}/frontend/user/trezor-session`;
  private isBrowser: boolean;

  constructor(private guiParams: GuiParams, private http: HttpClient, @Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  public login(loginData: UserLoginDto): Observable<SuccessLoginDto> {
    // We need to encode password because Angular cannot send it with special characters (e.g. + as the last character)
    const body = new HttpParams()
      .set('username', loginData.email)
      .set('password', encodeURIComponent(loginData.password));

    return this.sendAuthRequest(this.loginEndpoint, body);
  }

  public loginByTrezor(loginData: TrezorRegistrationDto): Observable<SuccessLoginDto> {
    const body = new HttpParams({
      fromObject: {
        ...pickBy((x) => x !== undefined, loginData),
      },
    });

    return this.sendAuthRequest(this.trezorLoginEndpoint, body);
  }

  private sendAuthRequest(endpoint: string, body: HttpParams): Observable<SuccessLoginDto> {
    // we need to obtain current csrf token
    return this.guiParams.getCurrentAndSaveToStore().pipe(
      switchMap((guiParams) => {
        const utmParams = this.getUtmParams();
        let requestParams = new HttpParams();
        Object.entries(utmParams).forEach(([key, value]) => {
          if (value !== null) {
            requestParams = requestParams.set(key, value);
          }
        });

        const httpOptions = {
          headers: new HttpHeaders()
            .append('Content-Type', 'application/x-www-form-urlencoded; charset=UTF-8')
            .append(CSRF_HEADER_NAME, guiParams.csrfToken || ''),
          params: requestParams,
        };

        return this.http.post<SuccessLoginDto>(endpoint, body, httpOptions).pipe(
          tap(() => {
            this.clearAnalyticalInfo();
          }),
          switchMap((loginResponse) => {
            // Refetch GUI parameters after successful login
            return this.guiParams.getCurrentAndSaveToStore().pipe(map(() => loginResponse));
          }),
        );
      }),
    );
  }

  private getUtmParams(): UtmParams {
    const utmFields = ['utmSource', 'utmMedium', 'utmCampaign', 'utmContent', 'utmTerm'];

    return utmFields.reduce((params, field) => {
      const value = this.safelyGetItemFromLocalStorage(`analytics.${field}`);
      if (value) {
        // Convert camelCase to snake_case for query params
        const paramName = `utm_${field.substring(3).toLowerCase()}`;
        params[paramName] = value;
      }
      return params;
    }, {} as UtmParams);
  }

  private safelyRemoveItemFromLocalStorage(key: string): void {
    if (!this.isBrowser) {
      return;
    }

    try {
      window.localStorage.removeItem(key);
    } catch (error) {
      console.warn('Unable to access localStorage:', error);
    }
  }

  private safelyGetItemFromLocalStorage(key: string): string | null {
    if (!this.isBrowser) {
      return null;
    }

    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      console.warn('Unable to access localStorage:', error);
      return null;
    }
  }

  private clearAnalyticalInfo() {
    const utmFields = ['utmSource', 'utmMedium', 'utmCampaign', 'utmContent', 'utmTerm'];
    utmFields.forEach((field) => {
      this.safelyRemoveItemFromLocalStorage(`analytics.${field}`);
    });
  }
}
